
export interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterCard?: string;
  canonicalUrl?: string;
  structuredData?: any;
}

export const updateSEO = (seoData: SEOData) => {
  // Update title
  document.title = seoData.title;

  // Update or create meta tags
  const updateMetaTag = (name: string, content: string, property?: boolean) => {
    const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
    let meta = document.querySelector(selector) as HTMLMetaElement;
    
    if (!meta) {
      meta = document.createElement('meta');
      if (property) {
        meta.setAttribute('property', name);
      } else {
        meta.setAttribute('name', name);
      }
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  // Basic meta tags
  updateMetaTag('description', seoData.description);
  if (seoData.keywords) {
    updateMetaTag('keywords', seoData.keywords);
  }

  // Open Graph tags
  updateMetaTag('og:title', seoData.ogTitle || seoData.title, true);
  updateMetaTag('og:description', seoData.ogDescription || seoData.description, true);
  updateMetaTag('og:type', 'website', true);
  updateMetaTag('og:url', seoData.canonicalUrl || window.location.href, true);
  
  if (seoData.ogImage) {
    updateMetaTag('og:image', seoData.ogImage, true);
  }

  // Twitter Card tags
  updateMetaTag('twitter:card', seoData.twitterCard || 'summary_large_image');
  updateMetaTag('twitter:title', seoData.ogTitle || seoData.title);
  updateMetaTag('twitter:description', seoData.ogDescription || seoData.description);
  
  if (seoData.ogImage) {
    updateMetaTag('twitter:image', seoData.ogImage);
  }

  // Canonical URL
  if (seoData.canonicalUrl) {
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', seoData.canonicalUrl);
  }

  // Structured Data
  if (seoData.structuredData) {
    // Remove existing structured data
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // Add new structured data (can be array or single object)
    const dataArray = Array.isArray(seoData.structuredData) ? seoData.structuredData : [seoData.structuredData];
    
    dataArray.forEach(data => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });
  }
};

export const generateOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "AIExchange",
    "description": "Premier AI Business Marketplace & Incubator for discovering, acquiring, and building AI companies",
    "url": siteUrl,
    "logo": `${siteUrl}/logo.png`,
    "sameAs": [],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "availableLanguage": "English"
    },
    "foundingDate": "2024",
    "industry": "Technology",
    "knowsAbout": ["Artificial Intelligence", "Business Acquisition", "Startup Incubation", "AI Marketplace"]
  };
};

export const generateWebsiteSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "AIExchange",
    "description": "Premier AI Business Marketplace & Incubator",
    "url": siteUrl,
    "publisher": {
      "@type": "Organization",
      "name": "AIExchange"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${siteUrl}/portal?search={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };
};

export const generateMarketplaceSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${siteUrl}/portal`,
    "name": "AI Business Portal - Verified Listings",
    "description": "Browse verified AI agencies, SaaS platforms, and automation businesses for acquisition",
    "url": `${siteUrl}/portal`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "AIExchange",
      "url": siteUrl
    },
    "mainEntity": {
      "@type": "ItemList",
      "name": "AI Business Listings",
      "description": "Curated collection of verified AI businesses for sale",
      "numberOfItems": 30,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "Product",
            "name": "AI Agency Listings",
            "description": "Verified AI agencies available for acquisition"
          }
        },
        {
          "@type": "ListItem", 
          "position": 2,
          "item": {
            "@type": "Product",
            "name": "SaaS Platform Listings",
            "description": "AI-powered SaaS platforms for sale"
          }
        }
      ]
    }
  };
};

export const generateServiceSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "AI Business Marketplace & Incubator",
    "description": "Connect buyers and sellers of AI businesses, provide incubation services for AI startups",
    "provider": {
      "@type": "Organization",
      "name": "AIExchange",
      "url": siteUrl
    },
    "areaServed": "Worldwide",
    "serviceType": "Business Marketplace",
    "url": siteUrl,
    "offers": {
      "@type": "Offer",
      "name": "AI Business Listing Service",
      "description": "List and discover AI businesses for acquisition"
    }
  };
};

export const generateExchangeSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${siteUrl}/exchange`,
    "name": "AI Business Exchange - Exclusive Marketplace",
    "description": "Join our exclusive exchange to access verified AI businesses and connect with serious buyers and sellers",
    "url": `${siteUrl}/exchange`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "AIExchange",
      "url": siteUrl
    },
    "mainEntity": {
      "@type": "Service",
      "name": "AI Business Exchange Membership",
      "description": "Exclusive access to verified AI business listings and acquisition opportunities",
      "provider": {
        "@type": "Organization",
        "name": "AIExchange"
      },
      "serviceType": "Membership Service",
      "areaServed": "Worldwide"
    }
  };
};

export const generateSubmitSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${siteUrl}/submit`,
    "name": "Submit Your AI Agency for Sale",
    "description": "List your AI agency or business for sale on our exclusive marketplace",
    "url": `${siteUrl}/submit`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "AIExchange",
      "url": siteUrl
    },
    "mainEntity": {
      "@type": "Service",
      "name": "AI Business Listing Service",
      "description": "Professional listing service for AI agencies and businesses seeking acquisition",
      "provider": {
        "@type": "Organization",
        "name": "AIExchange"
      },
      "serviceType": "Business Listing",
      "areaServed": "Worldwide"
    }
  };
};

export const generateBreadcrumbSchema = (items: Array<{name: string, url: string}>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url.startsWith('http') ? item.url : `${siteUrl}${item.url}`
    }))
  };
};
